import React, { Fragment, useState } from 'react';
import { useMutation, useQuery } from 'react-apollo';

import getAlbumFolderMarketingMaterials from '../../graphql/getAlbumFolderMarketingMaterials';
import accountMarketingMaterialCreateMutation from '../../graphql/accountMarketingMaterialCreate';
import sleep from '../../../../../../../util/sleep';
import { accountMarketingMaterialHasAssets } from '../../helpers';
import useOpenAlert from '../../../../../../../hooks/useOpenAlert';
import { getErrorString } from '../../../../../../../util/normalize-errors';
import { MeQuery } from '../../../../../../../graphql/queries/account';
import Button from '../../../../../../../components/global/Button';
import useBasename from '../../../../../../../components/routing/useBasename';
import { MARKETING_MATERIAL_TYPE } from '../../constants';
import {
  getAvailableMarketingMaterialTypes,
  getEventSubtypeMarketingMaterialMetadata,
} from './util';
import { ALBUM_TYPE } from '../../../../../../../constants/albums';
import accountMarketingMaterialConfigurationSetMutation from '../../graphql/accountMarketingMaterialConfigurationSet';
import { isWeddingsContent } from '../../../../../../../util/album';

const availableMarketingMaterialTypes = [
  {
    type: MARKETING_MATERIAL_TYPE.ONBOARDING_FLYER,
    preview:
      '/marketing-material/MARKETING_FLYER_TEMPLATE_PREVIEW_1_SAMPLE.png',
    label: 'Flyer',
  },
  {
    type: MARKETING_MATERIAL_TYPE.BUSINESS_CARD,
    preview:
      '/marketing-material/MARKETING_BUSINESS_CARD_GENERAL_PREVIEW_SAMPLE.png',
    label: 'Business Card',
  },
  {
    type: MARKETING_MATERIAL_TYPE.ONBOARDING_SOCIAL,
    preview: '/marketing-material/MARKETING_SOCIAL_TEMPLATE_PREVIEW_1.png',
    label: 'Social Post',
  },
  {
    type: MARKETING_MATERIAL_TYPE.PROFILE_PAGE_SOCIAL,
    preview:
      '/marketing-material/MARKETING_SOCIAL_PROFILE_PAGE_TEMPLATE_PREVIEW_1_SAMPLE.png',
    label: 'Profile Page',
  },
  {
    type: MARKETING_MATERIAL_TYPE.WEDDING_TABLE_TENT,
    preview:
      '/marketing-material/MARKETING_MATERIAL_WEDDING_TABLE_TENT_TEMPLATE_PREVIEW.png',
    vgb_preview:
      '/marketing-material/MARKETING_MATERIAL_WEDDING_TABLE_TENT_TEMPLATE_VGB_PREVIEW.png',
    label: 'Table Tent',
  },
  {
    type: MARKETING_MATERIAL_TYPE.WEDDING_FLYER,
    preview:
      '/marketing-material/MARKETING_MATERIAL_WEDDING_FLYER_TEMPLATE_PREVIEW.png',
    label: 'Venue Signage',
  },
  {
    type: MARKETING_MATERIAL_TYPE.WEDDING_FLYER_VIDEO_GUESTBOOK,
    preview:
      '/marketing-material/MARKETING_MATERIAL_WEDDING_FLYER_TEMPLATE_VGB_PREVIEW.png',
    label: 'Flyer Video Guestbook',
    condition: albumFolder => albumFolder?.has_video_album,
  },
  {
    type: MARKETING_MATERIAL_TYPE.SHUTTERBUG_TABLE_TENT,
    preview:
      '/marketing-material/MARKETING_MATERIAL_SHUTTERBUG_TABLE_TENT_TEMPLATE_PREVIEW.png',
    vgb_preview:
      '/marketing-material/MARKETING_MATERIAL_SHUTTERBUG_TABLE_TENT_TEMPLATE_VGB_PREVIEW.png',
    label: 'Table Tent',
  },
  {
    type: MARKETING_MATERIAL_TYPE.SHUTTERBUG_FLYER,
    preview:
      '/marketing-material/MARKETING_MATERIAL_SHUTTERBUG_FLYER_TEMPLATE_PREVIEW.png',
    vgb_preview:
      '/marketing-material/MARKETING_MATERIAL_SHUTTERBUG_FLYER_TEMPLATE_VGB_PREVIEW.png',
    label: 'Flyer / Social post',
  },
  {
    type: MARKETING_MATERIAL_TYPE.EVENT_TABLE_TENT,
    preview:
      '/marketing-material/MARKETING_MATERIAL_WEDDING_TABLE_TENT_TEMPLATE_PREVIEW.png',
    vgb_preview:
      '/marketing-material/MARKETING_MATERIAL_WEDDING_TABLE_TENT_TEMPLATE_VGB_PREVIEW.png',
    label: 'Table Tent',
  },
  {
    type: MARKETING_MATERIAL_TYPE.EVENT_FLYER,
    label: 'Flyer / Social post',
    getPreview: albumFolder => {
      return getEventSubtypeMarketingMaterialMetadata(albumFolder).preview;
    },
  },
];

const useAlbumFolderMarketingAssets = ({ albumFolderId }) => {
  const [assetsCreating, setAssetsCreating] = useState([]);
  const { data: { me: account } = {} } = useQuery(MeQuery);
  const {
    data: { albumFolder } = {},
    loading,
    refetch: refetchMarketingMaterials,
  } = useQuery(getAlbumFolderMarketingMaterials, {
    variables: {
      albumFolderId,
    },
    skip: !albumFolderId,
    fetchPolicy: 'cache-and-network',
    onCompleted: async data => {
      // Makes sure that all assets are generated
      const marketingMaterials =
        data?.albumFolder?.marketing_materials?.records;
      const marketingMaterialsHaveAssets = (marketingMaterials ?? []).filter(
        marketingMaterial =>
          !accountMarketingMaterialHasAssets(marketingMaterial),
      );

      if (
        marketingMaterials?.length > 0 &&
        marketingMaterialsHaveAssets.length > 0
      ) {
        setAssetsCreating(
          marketingMaterialsHaveAssets.map(
            marketingMaterial => marketingMaterial.id,
          ),
        );

        await sleep(3000);
        await refetchMarketingMaterials();
      } else if (assetsCreating) {
        setAssetsCreating([]);
      }
    },
  });
  const [accountMarketingMaterialCreate] = useMutation(
    accountMarketingMaterialCreateMutation,
  );
  const [accountMarketingMaterialConfigurationSet] = useMutation(
    accountMarketingMaterialConfigurationSetMutation,
  );
  const openAlert = useOpenAlert();
  const basename = useBasename();

  const onMarketingMaterialsAssetGenerate = async ({
    type,
    dismissed,
    configuration = null,
  }) => {
    const isWeddings = isWeddingsContent(albumFolder);
    const isEvents = albumFolder?.album_type === ALBUM_TYPE.EVENT;

    if (!account?.logo_image?.url && !dismissed && !isWeddings && !isEvents) {
      openAlert({
        type: 'info',
        title: 'Missing logo',

        message: ({ onRequestClose }) => (
          <Fragment>
            <p className="mt-1">
              Your logo can be printed on your marketing asset.
            </p>
            <p>Would you like to upload your logo now?</p>

            <div className="text-center">
              <Button
                btnStyle="link"
                onClick={() => {
                  onRequestClose();
                }}
                className="px-4"
                to={`/app/${basename}/account/settings/branding`}
              >
                Upload logo &rarr;
              </Button>
            </div>
          </Fragment>
        ),
        dismissible: true,
        onDismiss: () => {
          onMarketingMaterialsAssetGenerate({
            type,
            dismissed: true,
            configuration,
          });
        },
        btnLabel: 'Continue without a logo',
      });
      return;
    }

    try {
      // 1. Create the AccountMarketingMaterial
      const response = await accountMarketingMaterialCreate({
        variables: {
          input: {
            album_folder_id: albumFolderId,
            type,
          },
        },
      });

      if (configuration) {
        // Response here is pretty much irrelevant.
        await accountMarketingMaterialConfigurationSet({
          variables: {
            input: {
              album_folder_id: albumFolderId,
              marketing_material_type: type,
              ...configuration,
            },
          },
        });
      }

      // Set the newly created ID so that we know which one is being generated.
      setAssetsCreating([
        response.data.accountMarketingMaterialCreate.account_marketing_material
          .id,
      ]);

      // 2. Trigger refetching of marketing materials until print assets are generated.
      // The onComplete callback of the getAlbumFolderMarketingMaterials takes care of this.
      await refetchMarketingMaterials();
    } catch (error) {
      setAssetsCreating([]);

      openAlert({
        message: 'There was an error while generating your marketing assets',
        info: getErrorString(error),
      });
    }
  };

  const availableMarketingMaterialTypesByAlbumFolder =
    getAvailableMarketingMaterialTypes(albumFolder);

  return {
    albumFolder,
    marketingMaterials: albumFolder?.marketing_materials?.records ?? [],
    loading,
    assetsCreating,
    onMarketingMaterialsAssetGenerate,
    refetchMarketingMaterials,
    account,
    availableMarketingMaterialTypes: availableMarketingMaterialTypes.filter(
      item => {
        return (
          availableMarketingMaterialTypesByAlbumFolder.includes(item.type) &&
          // Only show the MarketingMaterial if it passes the condition
          // (if a check is available).
          (item?.condition?.(albumFolder) ?? true)
        );
      },
    ),
  };
};

export default useAlbumFolderMarketingAssets;
